import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  TooltipProps,
} from 'recharts';

import { ReactComponent as CloseCircle } from '../../../assets/icons/close-circle.svg';
import NoDataFoundImage from '../../../assets/images/no-data-found-vlb.png';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRange from '../../../shared-components/custom-date-range/CustomDateRange';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import { CapitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import vlbcApi from '../../../utils/api/vlbc';

import DeviceCharger from './DeviceOverView';

interface SidePanelProperties {
  closeModal: () => void;
  vlbcId: string;
  vlbcDetails: {
    loadBalancerName: string;
    algorithm: string;
  };
}

interface ChartDataItem {
  vlbcId?: string;
  label?: string;
  totalPowerInUse?: string | number;
  totalAvailablePower?: string | number;
  [key: string]: any; // Dynamic connector keys
}

interface OcppLogsProperties {
  id: string;
  _id: string;
  logType: 'in' | 'out';
  createdAt: string;
  message: string;
}

const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#d0ed57'];

interface ChartData {
  _id: string;
  eventType: string;
  totalAvailablePower: number;
  totalPowerInUse: number;
  maximumOutputPower: number;
  maximumOutputPowerUnit: string;
  chargers: {
    chargingPointSn: string;
    chargingPointName: string;
  }[];
  ocppLogs: OcppLogsProperties[];
  vlbcId: string;
}

/**
 * Function to get bar-chart keys
 * @param data
 * @returns
 */
const getAllBarKeys = (data: ChartDataItem[]): string[] => {
  if (!data || !Array.isArray(data)) return []; // Handle undefined/null/non-array

  const allKeys = new Set<string>();

  data.forEach((item) => {
    Object.keys(item || {}).forEach((key) => {
      if (!['vlbId', 'label', 'totalPowerInUse', 'totalAvailablePower'].includes(key)) {
        allKeys.add(key);
      }
    });
  });

  return Array.from(allKeys);
};

/**
 * side panel component
 *
 */
function SidePanel({ closeModal, vlbcId, vlbcDetails }: SidePanelProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('vlbc');
  const [hideOcppTable, setHideOcppTable] = useState<boolean>(true);
  const [ocppLogs, setOcppLogs] = useState<OcppLogsProperties[]>([]);
  const [containerWidth, setContainerWidth] = useState<string>('');
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [compositeChartData, setCompositeChartData] = useState<ChartDataItem[]>([]);
  const [barChartKeys, setBarChartKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false); // Step 1: Add loading state
  // const [chargerNames, setChargerName] = useState<string[]>([]);
  /**
   * Returns a formatted date string in YYYY-MM-DD format.
   * @param date - The date to format.
   * @returns The formatted date string.
   */
  const getFormattedDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const [dateFilter, setDateFilter] = useState<{
    [key: string]: string | undefined | number;
  }>({
    startDate: getFormattedDate(yesterday),
    endDate: getFormattedDate(today),
  });

  /**
   * Fetch vlbc
   * @param vlbcParameterId
   * @param query
   */
  const fetchVlbc = (vlbcParameterId: string, query: {}) => {
    setLoading(true);
    vlbcApi
      .fetchVlbcLogs(vlbcParameterId, query)
      .then((response: any) => {
        const transformed = response.data.map((item: any) => {
          // Clone the item so we don't mutate original
          const newItem = {
            ...item,
          };

          // Add new keys based on chargers array
          item.chargers.forEach((charger: any, index: number) => {
            // If you want to add suffix number for duplicate names like "Single connector 1"
            const keyName =
              index === 0 ? charger.chargingPointName : `${charger.chargingPointName}`;

            newItem[keyName] = charger.deRatedValue;
          });

          const totalAvailablePower = item?.totalAvailablePower || 0;
          const maximumOutputPower = item?.maximumOutputPower || 0;
          const reservedPower =
            totalAvailablePower < maximumOutputPower ? maximumOutputPower - totalAvailablePower : 0;

          newItem.reservedPower = reservedPower;
          newItem.fillStart = totalAvailablePower;

          return newItem;
        });

        // const chargerNamesTemporary =
        //   response.data[0]?.chargers.map((charger: any) => ({
        //     key: charger._id,
        //     name: charger.chargingPointName,
        //   })) ?? [];

        // setChargerName(chargerNamesTemporary);
        setChartData(transformed);
        setContainerWidth(`${response.data.length * 150}px`);

        const compositeChartArray = response?.data?.map((eachVlb: any, index: number) => {
          const eachObject: any = {
            eventType: eachVlb?.eventType,
            vlbcId: eachVlb._id || `vlb-${index}`,
            label: eachVlb?.eventType || '',
            totalPowerInUse: eachVlb?.totalPowerInUse || 0,
            totalAvailablePower: eachVlb?.totalAvailablePower || 0,
          };

          eachVlb?.chargers?.forEach((charger: any, subIndex: number) => {
            eachObject[charger?.chargingPointName || `charger-${subIndex}`] = charger.deRatedValue;
          });

          return eachObject;
        });

        setCompositeChartData(compositeChartArray);

        // getAllBarKeys returns an array of strings (key names)
        const allKeys = getAllBarKeys(compositeChartArray);
        const filteredKeys = allKeys.filter((key) => key !== 'eventType' && key !== 'vlbcId');
        setBarChartKeys(filteredKeys);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      })
      .finally(() => {
        setLoading(false); // Step 3: Stop loading
      });
  };

  /**
   * handle bar chart click event
   * @param event
   */
  const handleBarClick = (data: any) => {
    const logsToShow =
      chartData?.find((eachData) => eachData?._id === data?.vlbcId)?.ocppLogs || [];
    if (!logsToShow?.length) {
      return;
    }
    setHideOcppTable(false);
    setOcppLogs(
      logsToShow.map((log) => ({
        ...log,
        date: ConvertUTCtoDate(log?.createdAt || ''),
        time: FormatUtcString(ConvertUTCtoHHMM(log?.createdAt || '')),
      })),
    );
  };

  /**
   * Handle change date filters
   * @param name
   * @param value
   */
  const handleChangeDateFilters = (name: string, value: string | number) => {
    setDateFilter((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const column = [
    // {
    //   label: 'Date',
    //   accessorKey: 'date' as keyof OcppLogsProperties,
    //   sortAllow: false,
    //   columnWidth: 40,
    // },
    // {
    //   label: 'Time',
    //   accessorKey: 'time' as keyof OcppLogsProperties,
    //   sortAllow: false,
    //   columnWidth: 40,
    // },
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 40,
    },
    {
      label: t('type'),
      accessorKey: 'logType' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 20,
      /**
       * Display type in ocpp table
       * @param row
       * @returns
       */
      customData: (row: OcppLogsProperties) => (
        <div className={`ocpp-logs-type ${row?.logType}`}>
          <p>{CapitalizeFirstLetter(row?.logType)}</p>
        </div>
      ),
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof OcppLogsProperties,
      sortAllow: false,
    },
  ];

  /**
   * Function to show custom tooltip
   * @param active Whether tooltip is active
   * @param payload The data payload
   * @returns JSX element or null
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: 'white',
          padding: 10,
          border: '1px solid #ccc',
          borderRadius: 4,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {barChartKeys.map((key) => {
          const payloadItem = payload.find((p) => p.dataKey === key);
          if (!payloadItem) return null;

          return (
            <p key={key} style={{ color: payloadItem.color }}>
              {key}: {data[key]}
            </p>
          );
        })}
        <p style={{ color: '#FFD000' }}>Total Available Power: {data?.totalAvailablePower}</p>
        <p style={{ color: '#5D5DE4' }}>Total Power Inuse: {data?.totalPowerInUse}</p>
      </div>
    );
  }

  useEffect(() => {
    if (dateFilter.startDate && dateFilter.endDate) {
      fetchVlbc(vlbcId, dateFilter);
    }
  }, [dateFilter]);

  return (
    <div className="side-panel-wrap">
      <div className="side-panel-container">
        <div className="title">
          <div className="title-name">{t('loadBalancerInfo')}</div>
          <CloseCircle className="close-icon" onClick={closeModal} />
        </div>
        <div className="body">
          <div className="section-form">
            <CustomInput
              name="loadBalancerName"
              inputLabel={t('loadBalancerName')}
              type="text"
              placeholder=""
              value={vlbcDetails.loadBalancerName}
              onChangeValue={() => {}}
              readOnly
            />
            <CustomInput
              name="algorithm"
              inputLabel={t('algorithm')}
              type="text"
              placeholder=""
              value={vlbcDetails.algorithm}
              onChangeValue={() => {}}
              readOnly
            />
          </div>
          <div className="filter">
            <CustomDateRange
              onChangeValue={(name, value) => handleChangeDateFilters(name, value)}
              inputLabel=""
              value1={dateFilter?.startDate || ''}
              value2={dateFilter?.endDate || ''}
              name1="startDate"
              name2="endDate"
            />
          </div>

          {(() => {
            let cardView = null;

            if (loading) {
              cardView = (
                <div className="table-loading">
                  <BatteryLoader />
                </div>
              );
            } else if (compositeChartData?.length > 0) {
              cardView = (
                <div className="chart-wrapper">
                  <div className="chart-container" style={{ width: containerWidth }}>
                    <ResponsiveContainer width="100%" height={275}>
                      <ComposedChart
                        data={compositeChartData}
                        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis
                          dataKey="eventType"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10 }} // 👈 Smaller font size
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10 }}
                          // domain={[0, Number(chartData?.[0]?.maximumOutputPower)]} // 👈 Y-axis max set to 100
                        />
                        <Tooltip content={<CustomTooltip />} />

                        <Legend
                          verticalAlign="bottom"
                          height={30}
                          content={({ payload }) => (
                            <div className="custom-legend">
                              {/* eslint-disable react/prop-types */}
                              {payload
                                ?.filter(
                                  (entry) =>
                                    ![
                                      'reservedPower',
                                      'totalPowerInUse',
                                      'totalAvailablePower',
                                    ].includes(entry.value),
                                )
                                ?.map((entry) => (
                                  <span
                                    key={`item-${entry.toString()}`}
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: entry.color,
                                      }}
                                    />
                                    <span style={{ color: entry.color, fontSize: '12px' }}>
                                      {entry.value}
                                    </span>
                                  </span>
                                ))}
                              <span className="legend-item amt">
                                <div className="icon-box">
                                  <div className="bar"> </div>
                                </div>
                                <span>{t('using')}</span>
                              </span>
                              <span className="legend-item using">
                                <div className="icon-box">
                                  <div className="bar"> </div>
                                </div>
                                <span>{t('available')}</span>
                              </span>
                            </div>
                          )}
                        />

                        {barChartKeys.map((key, index) => (
                          <Bar
                            dataKey={key}
                            fill={colors[index % colors.length]}
                            barSize={20}
                            onClick={handleBarClick}
                          />
                        ))}

                        <Area
                          type="monotone"
                          dataKey="fillStart"
                          stackId="gray"
                          stroke="none"
                          fill="transparent"
                        />

                        <Area
                          type="monotone"
                          dataKey="reservedPower"
                          stackId="gray"
                          stroke="none"
                          fill="rgba(200, 200, 200, 0.5)"
                          isAnimationActive={false}
                        />

                        <Line
                          type="monotone"
                          dataKey="totalPowerInUse"
                          stroke="#5D5DE4"
                          dot={{ r: 2 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="totalAvailablePower"
                          stroke="#FFD000"
                          dot={{ r: 2 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            } else if (compositeChartData?.length === 0) {
              cardView = (
                <div className="chart-no-data-found-wrap">
                  <img src={NoDataFoundImage} alt="" className="chart-no-data-found-image" />
                  <div className="content">
                    <h5>{t('noDataFound')}</h5>
                    <p>{t('noDataDescription')}</p>
                  </div>
                </div>
              );
            }

            return cardView;
          })()}
        </div>
        {!hideOcppTable && (
          <div className="side-panel-ocpp-log-wrap">
            <div className="title">
              <div className="title-name">{t('ocppLogs')}</div>
              <CloseCircle className="close-icon" onClick={() => setHideOcppTable(true)} />
            </div>
            <div className="side-panel-ocpp-log-container">
              <CustomTable
                header={column}
                data={ocppLogs}
                loading={false}
                filterAllow={false}
                sizeChangeButtonRequired={false}
                limit={0}
                currentPage={0}
                totalPages={0}
                totalResults={0}
                handlePageChange={() => {}}
                handleLimitChange={() => {}}
              />
            </div>
          </div>
        )}
        <div style={{ overflowY: 'auto' }}>
          <DeviceCharger vlbcId={vlbcId} />
        </div>
      </div>
    </div>
  );
}

export default SidePanel;
